import { useCallback, useEffect, useState } from "react";
import { Box, Spinner } from "@cloudscape-design/components";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { listActionItems } from "./core/api/action-items";
import { listReports } from "./sections/scope/api";
import { AuthProvider, useAuth } from "./core/auth";
import { initTheme, setTheme, type ThemeMode } from "./core/ui/theme";
import { ActionItemsInbox } from "./shell/ActionItemsInbox";
import { ActivityLog } from "./shell/ActivityLog";
import { ChatDrawerToggle, useChatDrawer } from "./shell/ChatDrawer";
import { ChatPanel } from "./shell/ChatPanel";
import { CompanyProfile } from "./shell/CompanyProfile";
import { HowItWorks } from "./shell/HowItWorks";
import { Login } from "./shell/Login";
import { RcmLibrary } from "./shell/RcmLibrary";
import { ReportDetail } from "./shell/ReportDetail";
import { SideNav } from "./shell/SideNav";
import { TermsFooter } from "./shell/TermsFooter";
import { TermsGate } from "./shell/TermsGate";
import { ReportsList } from "./sections/scope/ReportsList";
import "./App.css";

/** Root: gate the app behind Cognito sign-in (Slice AUTH). */
export function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <Box padding="xxl" textAlign="center">
        <Spinner size="large" /> Loading…
      </Box>
    );
  }
  return user ? (
    <TermsGate>
      <AppShell />
    </TermsGate>
  ) : (
    <Login />
  );
}

/**
 * 3-pane layout: SideNav | content | ChatPanel.
 *
 * Routing is a state machine (v2 pattern). URLs never change; deep-linking
 * is deferred until customers ask. Action items is the landing view.
 *
 * Cloudscape's AppLayout doesn't support drag-to-resize between side panels,
 * so we use react-resizable-panels for the column split. Every component
 * INSIDE a panel remains Cloudscape.
 */
type View =
  | { kind: "reports" }
  | { kind: "report-detail"; reportId: string; focusActionItemId?: string }
  | { kind: "inbox" }
  | { kind: "rcm" }
  | { kind: "profile" }
  | { kind: "activity" }
  | { kind: "how-it-works" };

type NavTarget = "reports" | "inbox" | "rcm" | "profile" | "activity" | "how-it-works";

const OPEN_COUNT_POLL_MS = 10_000;

function AppShell() {
  const { signOut } = useAuth();
  const [view, setView] = useState<View>({ kind: "inbox" });
  const [theme, setThemeState] = useState<ThemeMode>(() => initTheme());
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [openReportsCount, setOpenReportsCount] = useState<number | null>(null);
  const chat = useChatDrawer();

  const openReport = (id: string) => setView({ kind: "report-detail", reportId: id });
  const openActionItem = (reportId: string, actionItemId: string) =>
    setView({ kind: "report-detail", reportId, focusActionItemId: actionItemId });

  const refreshOpenCount = useCallback(async () => {
    try {
      const [items, reports] = await Promise.all([listActionItems({ status: "open" }), listReports()]);
      setOpenCount(items.length);
      setOpenReportsCount(reports.filter((r) => r.lifecycle_status !== "complete").length);
    } catch {
      // Don't surface — the badges just stay as-is.
    }
  }, []);

  useEffect(() => {
    void refreshOpenCount();
    const id = setInterval(() => void refreshOpenCount(), OPEN_COUNT_POLL_MS);
    return () => clearInterval(id);
  }, [refreshOpenCount, view]);

  const toggleTheme = () => {
    const next: ThemeMode = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  };

  let center;
  switch (view.kind) {
    case "reports":
      center = (
        <ReportsList onSelectReport={openReport} onNavigateProfile={() => setView({ kind: "profile" })} />
      );
      break;
    case "report-detail":
      center = (
        <ReportDetail
          reportId={view.reportId}
          focusActionItemId={view.focusActionItemId}
          onBack={() => setView({ kind: "inbox" })}
          onOpenReport={openReport}
        />
      );
      break;
    case "inbox":
      center = (
        <ActionItemsInbox
          onOpenActionItem={openActionItem}
          onNavigateReports={() => setView({ kind: "reports" })}
        />
      );
      break;
    case "rcm":
      center = <RcmLibrary />;
      break;
    case "profile":
      center = <CompanyProfile />;
      break;
    case "activity":
      center = <ActivityLog />;
      break;
    case "how-it-works":
      center = <HowItWorks />;
      break;
  }

  const activeNav: NavTarget =
    view.kind === "inbox"
      ? "inbox"
      : view.kind === "rcm"
        ? "rcm"
        : view.kind === "profile"
          ? "profile"
          : view.kind === "activity"
            ? "activity"
            : view.kind === "how-it-works"
              ? "how-it-works"
              : "reports";

  return (
    <div className="app-shell">
      <PanelGroup
        direction="horizontal"
        autoSaveId={chat.open ? "soc1lab-v3-layout-3col" : "soc1lab-v3-layout-2col"}
      >
        <Panel defaultSize={chat.open ? 18 : 22} minSize={14} maxSize={32}>
          <div className="app-pane">
            <SideNav
              openReportsCount={openReportsCount}
              active={activeNav}
              openCount={openCount}
              theme={theme}
              onToggleTheme={toggleTheme}
              onNavigate={(target) => setView({ kind: target } as View)}
              onSignOut={() => void signOut()}
            />
          </div>
        </Panel>

        <PanelResizeHandle className="app-resize-handle" />

        <Panel defaultSize={chat.open ? 56 : 78} minSize={40}>
          <div className="app-pane app-pane--content">
            {!chat.open && (
              <div className="app-content-toolbar">
                <ChatDrawerToggle open={chat.open} onToggle={chat.toggle} />
              </div>
            )}
            {center}
          </div>
        </Panel>

        {chat.open && (
          <>
            <PanelResizeHandle className="app-resize-handle" />
            <Panel defaultSize={26} minSize={18} maxSize={40}>
              <div className="app-pane app-pane--chat">
                <ChatPanel onClose={chat.close} />
              </div>
            </Panel>
          </>
        )}
      </PanelGroup>
      <TermsFooter />
    </div>
  );
}
